import { Component, OnInit, AfterViewInit, OnDestroy, ElementRef, ViewChild, Inject, PLATFORM_ID, ChangeDetectorRef } from "@angular/core";
import { CommonModule, isPlatformBrowser } from "@angular/common";
import { LibraryModule } from "../../app/shared/lib/lib.module";
import { Router } from "@angular/router";
import { EventService, Event } from "../services/event.service";
import { ApiService } from "../core/services/api.service";
import { UserService } from "../core/services/user.service";
import { API_ENDPOINTS } from "../core/constants/api-endpoints";
import { getProgramId, getUserDetails } from "../shared/utils/storage-utils";
import { pageLoadAnimationBottomToCenter, fadeInAnimation } from "../shared/utils/common-animations";
import { ImagePreloadService } from "../shared/services/image-preload.service";
import { LanguageService } from "../core/services/language.service";
import { Subscription } from "rxjs";

@Component({
  selector: "upcoming-events-component, UpcomingEventsComponent",
  standalone: true,
  templateUrl: './upcoming-events.component.html',
  styleUrls: ['./upcoming-events.component.css'],
  imports: [CommonModule, LibraryModule],
  animations: [pageLoadAnimationBottomToCenter, fadeInAnimation]
})
export class UpcomingEventsComponent implements OnInit, AfterViewInit, OnDestroy {
  pageLoadState = 'hidden';
  events: Event[] = [];
  isLoading = true;
  hasError = false;
  isSubmitting = false;
  imagesLoaded: { [key: string]: boolean } = {};
  private readonly imageUrlCache = new Map<string, string>();
  preloadedImageUrls = new Map<string, string>();
  @ViewChild('eventsScroll') private readonly eventsScroll?: ElementRef<HTMLDivElement>;
  private isScrolling = false;
  canScrollLeft = false;
  canScrollRight = true;
  private scrollListener?: () => void;
  private resizeListener?: () => void;
  private activitiesSelected: boolean = false;
  registrationError = '';
  private languageSub?: Subscription;

  constructor(
    private readonly router: Router,
    private readonly eventService: EventService,
    private readonly apiService: ApiService,
    private readonly userService: UserService,
    @Inject(PLATFORM_ID) private readonly platformId: Object,
    private readonly cdr: ChangeDetectorRef,
    private readonly imagePreloadService: ImagePreloadService,
    private readonly languageService: LanguageService
  ) {}

  ngOnInit(): void {
    this.languageSub = this.languageService.language$.subscribe(() => this.cdr.markForCheck());
    // Check if activities were selected from previous navigation
    // Use history.state as fallback for when navigation has completed
    const state = (history.state as { activitiesSelected?: boolean }) || {};
    this.activitiesSelected = state.activitiesSelected ?? false;
    this.loadEvents();
  }

  private getUserId(): number | null {
    let userId = this.userService.getStoredUserId();
    if (!userId) {
      const userDetails = getUserDetails();
      userId = userDetails?.value ?? userDetails?.id ?? userDetails?.userId;
    }
    const parsed = Number(userId);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }

  loadEvents(): void {
    this.isLoading = true;
    this.hasError = false;
    this.registrationError = '';
    const userId = this.getUserId();
    this.eventService.getUpcomingEvents(0, 10, userId ?? undefined).subscribe({
      next: (events) => {
        this.events = events;
        this.isLoading = false;
        // Reset image loading states for new events
        this.imagesLoaded = {};
        this.preloadedImageUrls.clear();
        this.imageUrlCache.clear();
        
        // Preload images
        if (isPlatformBrowser(this.platformId)) {
          this.preloadEventImages();
        }
        
        // Check scroll position after events are loaded
        setTimeout(() => {
          this.checkScrollPosition();
        }, 100);
      },
      error: () => {
        this.hasError = true;
        this.isLoading = false;
        this.events = [];
      }
    });
  }

  private preloadEventImages(): void {
    if (this.events.length === 0) return;
    
    // Clear cache when loading new events
    this.imagePreloadService.clearCache();
    this.imageUrlCache.clear();
    this.preloadedImageUrls.clear();
    
    // Use image preload service (skip events with no thumbnail)
    this.imagePreloadService.preloadImages(
      this.events
        .filter((event) => !!event.image?.trim())
        .map((event) => ({ id: event.id, image: event.image })),
      this.imagesLoaded,
      this.cdr
    );
  }

  getImageUrl(eventId: string): string {
    return this.imagePreloadService.getImageUrl(eventId);
  }

  isImageLoaded(eventId: string): boolean {
    return this.imagesLoaded[eventId] === true;
  }

  handleImageLoad(eventId: string): void {
    this.imagesLoaded[eventId] = true;
  }

  handleImageError(eventId: string): void {
    this.imagesLoaded[eventId] = false;
  }

  ngAfterViewInit(): void {
    requestAnimationFrame(() => {
      setTimeout(() => {
        this.pageLoadState = 'visible';
        this.checkScrollPosition();
        this.setupScrollListener();
      }, 50);
    });
  }

  private setupScrollListener(): void {
    const container = this.eventsScroll?.nativeElement;
    if (container) {
      this.scrollListener = () => {
        this.checkScrollPosition();
      };
      this.resizeListener = () => {
        this.checkScrollPosition();
      };
      
      container.addEventListener('scroll', this.scrollListener);
      window.addEventListener('resize', this.resizeListener);
    }
  }

  ngOnDestroy(): void {
    this.languageSub?.unsubscribe();
    const container = this.eventsScroll?.nativeElement;
    if (container && this.scrollListener) {
      container.removeEventListener('scroll', this.scrollListener);
    }
    if (this.resizeListener) {
      window.removeEventListener('resize', this.resizeListener);
    }
  }

  private checkScrollPosition(): void {
    const container = this.eventsScroll?.nativeElement;
    if (!container) {
      return;
    }

    const scrollLeft = container.scrollLeft;
    const scrollWidth = container.scrollWidth;
    const clientWidth = container.clientWidth;
    const maxScroll = scrollWidth - clientWidth;

    // If content fits within viewport, disable both buttons
    if (scrollWidth <= clientWidth) {
      this.canScrollLeft = false;
      this.canScrollRight = false;
      return;
    }

    // Check if we can scroll left (not at the start)
    this.canScrollLeft = scrollLeft > 1; // Using 1 instead of 0 to account for sub-pixel rendering

    // Check if we can scroll right (not at the end)
    this.canScrollRight = scrollLeft < maxScroll - 1; // Using -1 to account for sub-pixel rendering
  }

  onViewEventClick(event: Event): void {
    this.eventService.setSelectedEvent(event.id);
    this.router.navigate(['/event-details'], {
      state: { activitiesSelected: this.activitiesSelected },
    });
  }

  onEventKeyDown(event: KeyboardEvent, eventData: Event): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.onViewEventClick(eventData);
    }
  }

  getRegisterButtonLabel(event: Event): string {
    if (event.isRegistered) {
      return this.languageService.t('events.registered');
    }
    if (event.isWaitlisted) {
      return this.languageService.t('events.waitlisted');
    }
    return this.languageService.t('events.register');
  }

  isEventActive(event: Event): boolean {
    return event.isRegistered === true || event.isWaitlisted === true;
  }

  /** Waiting List badge only — no Confirmed tag on the card image. */
  getEventCardBadge(event: Event): { label: string; tone: 'waitlist' } | null {
    if (event.isRegistered) {
      return null;
    }
    const waitlistCount = event.waitlistedCount ?? 0;
    const isFull = event.spotsRemaining != null && event.spotsRemaining <= 0;
    if (event.isWaitlisted || isFull || waitlistCount > 0) {
      return {
        label: this.languageService.t('events.waitingListCount', {
          count: waitlistCount,
        }),
        tone: 'waitlist',
      };
    }
    return null;
  }

  get upcomingTitle(): string {
    return this.languageService.t('events.upcoming');
  }

  onRegisterAction(event: Event): void {
    // Figma: Register or Registered opens event details; action happens on details page.
    this.registrationError = '';
    this.eventService.setSelectedEvent(event.id);
    this.router.navigate(['/event-details'], {
      state: { activitiesSelected: this.activitiesSelected },
    });
  }

  onBackClick(): void {
    this.router.navigate(['/activity-screen']);
  }

  onFinishClick(): void {
    const userId = this.getUserId();
    const programId = getProgramId();

    if (!userId || !programId) {
      return;
    }

    this.isSubmitting = true;

    const payload = {
      userId: Number(userId),
      programId: programId ?? 0,
    };

    this.apiService.post<{ statusCode: number; message: string }>(
      API_ENDPOINTS.STAFF.SUBMIT,
      payload
    ).subscribe({
      next: (response) => {
        this.isSubmitting = false;
        if (response.statusCode === 200) {
          this.router.navigate(['/success'], {
            state: { activitiesSelected: this.activitiesSelected },
          });
        }
      },
      error: () => {
        this.isSubmitting = false;
      },
    });
  }

  scrollLeft(): void {
    if (this.canScrollLeft) {
      this.scrollBy(-1);
    }
  }

  scrollRight(): void {
    if (this.canScrollRight) {
      this.scrollBy(1);
    }
  }

  private scrollBy(direction: 1 | -1): void {
    const container = this.eventsScroll?.nativeElement;
    if (!container || this.isScrolling) {
      return;
    }

    // Calculate scroll amount based on card width + gap for smoother slider transitions
    const cardsContainer = container.querySelector('div') as HTMLElement;
    let scrollAmount: number;
    
    if (cardsContainer) {
      const firstCard = cardsContainer.querySelector('div') as HTMLElement;
      if (firstCard) {
        const cardWidth = firstCard.offsetWidth;
        // Get computed gap from the container (handles responsive gaps)
        const computedStyle = window.getComputedStyle(cardsContainer);
        const gap = parseInt(computedStyle.gap) || 16;
        scrollAmount = cardWidth + gap;
      } else {
        scrollAmount = container.clientWidth * 0.8;
      }
    } else {
      scrollAmount = container.clientWidth * 0.8;
    }

    const startPosition = container.scrollLeft;
    const targetPosition = startPosition + (direction * scrollAmount);
    const distance = targetPosition - startPosition;
    const duration = 600; // 600ms for smooth slider transition
    let startTime: number | null = null;

    this.isScrolling = true;

    const animateScroll = (currentTime: number) => {
      if (startTime === null) {
        startTime = currentTime;
      }

      const timeElapsed = currentTime - startTime;
      const progress = Math.min(timeElapsed / duration, 1);
      
      // Easing function for smooth slider transition (ease-in-out cubic)
      const ease = progress < 0.5
        ? 4 * progress * progress * progress
        : 1 - Math.pow(-2 * progress + 2, 3) / 2;

      container.scrollLeft = startPosition + (distance * ease);

      if (progress < 1) {
        requestAnimationFrame(animateScroll);
      } else {
        this.isScrolling = false;
        this.checkScrollPosition(); // Update button states after scroll completes
      }
    };

    requestAnimationFrame(animateScroll);
  }
}
